/**
 * CDN entry: <script src=".../kockica.min.js" data-site-id="…" data-controller="…" data-policy-url="…"></script>
 * Put it first in <head>, before Google Tag Manager and any blocked script.
 * Exposes window.Kockica = { init, getApi } for advanced use.
 */
import { DVConsentConfigError, initConsent } from './index';
import type { DVConsentApi, DVConsentConfig, OptionalCategory, ServiceKey } from './types';

const list = (value: string | undefined): string[] =>
  (value ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

export function configFromScript(el: HTMLScriptElement): DVConsentConfig {
  const d = el.dataset;
  const config: DVConsentConfig = {
    siteId: d.siteId ?? '',
    controller: { name: d.controller ?? '' },
    policyUrl: d.policyUrl ?? '',
  };
  if (d.languages) config.languages = list(d.languages);
  if (d.services) config.services = list(d.services) as ServiceKey[];
  if (d.categories) config.categories = list(d.categories) as OptionalCategory[];
  if (d.logEndpoint) config.logEndpoint = d.logEndpoint;
  if (d.revision) config.revision = Number(d.revision);
  if (d.cookieDomain) config.cookieDomain = d.cookieDomain;
  if (d.layout === 'bar' || d.layout === 'box') config.layout = d.layout;
  if (d.consentMode === 'false') config.consentMode = false;
  if (d.revisitButton === 'false') config.revisitButton = false;
  if (d.revisitPosition === 'right') config.revisitPosition = 'right';
  if (d.branding === 'false') config.branding = false;
  if (d.respectGpc === 'true') config.respectGpc = true;
  if (d.autoBlock === 'false') config.autoBlock = false;
  if (d.googleMode === 'basic') config.googleMode = 'basic';
  return config;
}

function injectStylesheet(scriptSrc: string): void {
  if (!scriptSrc || document.querySelector('link[data-kockica-css]')) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = scriptSrc.replace(/kockica\.min\.js(\?.*)?$/, 'style.css$1');
  link.setAttribute('data-kockica-css', '');
  document.head.appendChild(link);
}

let api: DVConsentApi | null = null;

function init(config: DVConsentConfig): DVConsentApi {
  api = initConsent(config);
  return api;
}

const script = document.currentScript as HTMLScriptElement | null;
if (script && script.dataset.siteId !== undefined) {
  injectStylesheet(script.src);
  try {
    init(configFromScript(script));
  } catch (error) {
    // Fail loud in the console; the page itself keeps working with nothing optional loaded.
    if (error instanceof DVConsentConfigError) console.error(error.message);
    else throw error;
  }
}

export { init };
export const getApi = (): DVConsentApi | null => api;
